import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Event, GameConstant, LeaderboardPeriod, LeaderboardResponse } from "@uno/protocol";
import { AppContext } from "context";
import Container from "component/Layout/Container";
import ButtonHapticWrapper from "component/Button/ButtonHapticWrapper";
import useStorePlayer from "store/useStorePlayer";
import PeriodToggle from "./components/PeriodToggle";
import LeaderboardRow from "./components/LeaderboardRow";
import Podium from "./components/Podium";

const formatWeekRange = (startIso: string | null, endIso: string | null, locale: string): string => {
    if(!startIso || !endIso) {
        return "";
    }

    const formatter = new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" });
    const start = formatter.format(new Date(startIso));
    const endDate = new Date(endIso);
    endDate.setUTCDate(endDate.getUTCDate() - 1);
    const end = formatter.format(endDate);
    return `${start} – ${end}`;
};

const WeekArrow: React.FC<{ direction: "prev" | "next"; onClick: () => void; disabled?: boolean; label: string }> = ({ direction, onClick, disabled, label }) => (
    <ButtonHapticWrapper>
        <button
            onClick={onClick}
            disabled={disabled}
            aria-label={label}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 flex items-center justify-center text-white/80 cursor-pointer transition-colors active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
        >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                {direction === "prev"
                    ? <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    : <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />}
            </svg>
        </button>
    </ButtonHapticWrapper>
);

const BackButton: React.FC<{ onClick: () => void; label: string }> = ({ onClick, label }) => (
    <ButtonHapticWrapper>
        <button
            onClick={onClick}
            aria-label={label}
            className="flex items-center justify-center w-9 h-9 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full transition-colors cursor-pointer text-white/80"
        >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
        </button>
    </ButtonHapticWrapper>
);

const PodiumIcon: React.FC = () => (
    <svg
        className="w-10 h-10 md:w-12 md:h-12 drop-shadow-[0_4px_12px_rgba(251,191,36,0.5)]"
        viewBox="0 0 24 24"
        fill="url(#podium-gradient)"
        stroke="#78350f"
        strokeWidth="1.1"
        strokeLinejoin="round"
    >
        <defs>
            <linearGradient id="podium-gradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fde68a" />
                <stop offset="55%" stopColor="#fbbf24" />
                <stop offset="100%" stopColor="#d97706" />
            </linearGradient>
        </defs>
        <rect x="2" y="13" width="6" height="9" rx="1" />
        <rect x="9" y="6" width="6" height="16" rx="1" />
        <rect x="16" y="15" width="6" height="7" rx="1" />
    </svg>
);

const LoadingState: React.FC<{ label: string }> = ({ label }) => (
    <div className="flex flex-col items-center justify-center py-16 gap-3 text-white/50 text-sm">
        <svg className="animate-spin w-6 h-6 text-amber-300" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
            <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <span className="font-changa-one tracking-wide">{label}</span>
    </div>
);

const EmptyState: React.FC<{ label: string }> = ({ label }) => (
    <div className="flex flex-col items-center justify-center py-16 text-white/55 text-sm text-center px-6">
        <span className="font-changa-one tracking-wide max-w-xs">{label}</span>
    </div>
);

const Leaderboard: React.FC = () => {
    const { socket } = React.useContext(AppContext);
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const myPlayerId = useStorePlayer((state) => state.player?.id);

    const PAGE_SIZE = 20;
    const INITIAL_LIMIT = PAGE_SIZE;

    const [period, setPeriod] = React.useState<LeaderboardPeriod>("weekly");
    const [weekOffset, setWeekOffset] = React.useState(0);
    const [data, setData] = React.useState<LeaderboardResponse | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [limit, setLimit] = React.useState(INITIAL_LIMIT);
    const previousPeriod = React.useRef(period);
    const previousWeekOffset = React.useRef(weekOffset);

    React.useEffect(() => {
        if(!socket) {
            return;
        }

        const isReset = previousPeriod.current !== period
            || previousWeekOffset.current !== weekOffset
            || data === null;

        if(isReset) {
            setLoading(true);
        }

        previousPeriod.current = period;
        previousWeekOffset.current = weekOffset;

        socket.emit(Event.PlayerGetLeaderboard, { period, weekOffset, limit }, (response: LeaderboardResponse) => {
            setData(response);

            if(isReset) {
                setLoading(false);
            }
        });
    }, [socket, period, weekOffset, limit]);

    const handleChangePeriod = React.useCallback((next: LeaderboardPeriod) => {
        setPeriod(next);
        setWeekOffset(0);
        setLimit(INITIAL_LIMIT);
    }, []);

    const handleChangeWeek = React.useCallback((delta: number) => {
        setWeekOffset((offset) => Math.max(0, offset + delta));
        setLimit(INITIAL_LIMIT);
    }, []);

    const entries = data?.entries ?? [];
    const podium = entries.slice(0, 3);
    const rest = entries.slice(3);
    const hasMore = entries.length >= limit;
    const showPodium = !loading && podium.length > 0;
    const showRest = !loading && rest.length > 0;

    return (
        <Container
            id="leaderboard"
            headerLeft={<BackButton onClick={() => navigate("/")} label={t("leaderboard.back")} />}
        >
            <main className="flex-1 flex flex-col items-center gap-5 px-2 md:px-4 py-6 w-full max-w-xl mx-auto">
                <div className="flex flex-col items-center gap-2">
                    <PodiumIcon />
                    <h1 className="font-changa-one text-4xl md:text-5xl text-white tracking-[0.2em] uppercase drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
                        {t("leaderboard.title")}
                    </h1>
                </div>

                <p className="font-changa-one text-white/60 text-sm text-center max-w-md leading-relaxed tracking-wide -mt-3 mb-3 md:mb-6">
                    {t(period === "weekly" ? "leaderboard.subtitle_weekly" : "leaderboard.subtitle_alltime", {
                        count: GameConstant.leaderboardDailyCap,
                    })}
                </p>

                <div className="flex items-center gap-2">
                    {period === "weekly" && (
                        <WeekArrow
                            direction="prev"
                            onClick={() => handleChangeWeek(1)}
                            label={t("leaderboard.previous_week")}
                        />
                    )}

                    <PeriodToggle
                        active={period}
                        onChange={handleChangePeriod}
                        weekIsoNumber={data?.weekIsoNumber}
                    />

                    {period === "weekly" && (
                        <WeekArrow
                            direction="next"
                            onClick={() => handleChangeWeek(-1)}
                            disabled={weekOffset === 0}
                            label={t("leaderboard.next_week")}
                        />
                    )}
                </div>

                <AnimatePresence mode="wait" initial={false}>
                    {period === "weekly" && data?.weekStart && (
                        <motion.span
                            key={`week-range-${weekOffset}`}
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            transition={{ duration: 0.15 }}
                            className="text-white/55 text-[11px] tabular-nums -mt-2"
                        >
                            {formatWeekRange(data.weekStart, data.weekEnd, i18n.language)}
                        </motion.span>
                    )}
                </AnimatePresence>

                <div className="w-full flex-1">
                    {loading && <LoadingState label={t("leaderboard.loading")} />}

                    {!loading && entries.length === 0 && (
                        <EmptyState
                            label={period === "weekly"
                                ? t("leaderboard.empty_weekly")
                                : t("leaderboard.empty_alltime")}
                        />
                    )}

                    {showPodium && (
                        <Podium entries={podium} myPlayerId={myPlayerId} />
                    )}

                    {showRest && (
                        <ul className="flex flex-col gap-2 mt-2">
                            {rest.map((entry, index) => (
                                <LeaderboardRow
                                    key={entry.playerId}
                                    entry={entry}
                                    isMe={entry.playerId === myPlayerId}
                                    index={index}
                                />
                            ))}
                        </ul>
                    )}

                    {hasMore && !loading && (
                        <div className="flex justify-center mt-4">
                            <ButtonHapticWrapper>
                                <button
                                    type="button"
                                    onClick={() => setLimit((value) => value + PAGE_SIZE)}
                                    aria-label={t("leaderboard.load_more")}
                                    className="px-7 h-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 flex items-center justify-center text-white/70 cursor-pointer transition-colors active:scale-95"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                            </ButtonHapticWrapper>
                        </div>
                    )}
                </div>
            </main>
        </Container>
    );
};

export default Leaderboard;
